/**
 * Animations PUBLIÉES (lot 4, migration 0666) : ce que voient les élèves et
 * les visiteurs, sans compte.
 *
 *   - `get_card_animation(card)` : l'animation d'une SKILLCARD, en résumé
 *     (page de la carte, fenêtre « Cours ») ; le document n'est chargé qu'au
 *     lancement ;
 *   - `get_animation(slug)` : la version publiée. Sans accès à la SKILLCARD,
 *     le serveur sert le document GRATUIT (scènes « À toi » retirées) et la
 *     liste des scènes verrouillées : les réponses ne quittent jamais le
 *     serveur. L'accès dépend du compte : la clé de cache contient l'utilisateur.
 *
 * Réponses vérifiées par Zod (RPC non typées : hors des types générés).
 */

import { useQuery } from '@tanstack/react-query';
import type { SupabaseClient } from '@supabase/supabase-js';
import { z } from 'zod';
import { CARD_PUBLIC_ORIGIN } from '@jeraisonne/db/cards';
import { cardLinkOrigin } from '@/lib/cards/link-origins';
import { supabase } from '@/lib/supabase';

const db = supabase as unknown as SupabaseClient;

const lockedSceneSchema = z.object({
  id: z.string(),
  title: z.string(),
  hora: z.enum(['H', 'O', 'R', 'A']).optional(),
});

export const publishedAnimationSchema = z.object({
  id: z.string().uuid(),
  slug: z.string().min(1),
  card_id: z.string().uuid().nullable(),
  title: z.string(),
  version_no: z.number().int().min(1),
  format_version: z.number().int().min(1),
  runtime_min: z.number().int(),
  published_at: z.string(),
  access: z.object({
    interactive: z.boolean(),
    reason: z.string(),
    signed_in: z.boolean(),
  }),
  /** document JRAF : lu par `readPublishedJraf` (format, version du lecteur) */
  doc: z.unknown(),
  locked: z.array(lockedSceneSchema),
});
export type PublishedAnimation = z.infer<typeof publishedAnimationSchema>;

export const cardAnimationRefSchema = z.object({
  id: z.string().uuid(),
  slug: z.string().min(1),
  title: z.string(),
  version_no: z.number().int().min(1),
  runtime_min: z.number().int(),
  scenes: z.number().int().min(0),
  has_interactive: z.boolean(),
});
export type CardAnimationRef = z.infer<typeof cardAnimationRefSchema>;

export const publishedKeys = {
  bySlug: (slug: string, userId: string | null) =>
    ['animations', 'published', slug, userId ?? 'anon'] as const,
  ofCard: (cardId: string) => ['animations', 'published-card', cardId] as const,
};

/** Lien public d'une animation (partage, Shorts). */
export function animationPublicPath(slug: string): string {
  return `/animation/${encodeURIComponent(slug)}`;
}

/** Lien absolu à partager : origine publique (jamais `capacitor://localhost`), locale en dev. */
export function animationShareUrl(slug: string): string {
  return `${cardLinkOrigin() ?? CARD_PUBLIC_ORIGIN}${animationPublicPath(slug)}`;
}

/** Version publiée d'une animation, ou `null` (inconnue, retirée de l'app). */
export function usePublishedAnimation(slug: string, userId: string | null, enabled = true) {
  return useQuery({
    queryKey: publishedKeys.bySlug(slug, userId),
    enabled: enabled && slug.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<PublishedAnimation | null> => {
      const { data, error } = await db.rpc('get_animation', { p_slug: slug });
      if (error) throw error;
      if (data === null || data === undefined) return null;
      return publishedAnimationSchema.parse(data);
    },
  });
}

/** L'animation publiée d'une SKILLCARD (résumé), ou `null`. */
export function useCardAnimationRef(cardId: string, enabled = true) {
  return useQuery({
    queryKey: publishedKeys.ofCard(cardId),
    enabled: enabled && cardId.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<CardAnimationRef | null> => {
      const { data, error } = await db.rpc('get_card_animation', { p_card_id: cardId });
      if (error) throw error;
      if (data === null || data === undefined) return null;
      return cardAnimationRefSchema.parse(data);
    },
  });
}
